import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { LicenseSystemAccessSettings } from 'src/components/userManagement/UserManagement';
import AppContext, { Role } from '../AppContext';
import { GqlError } from './GqlError';
import { Role as BackendTypeRole, InviteUserInput, InvitedUserPayload, LicenseSystemAccessRole } from './generated/types';
import { ModuleId } from './mappers/module';

export const mutation = `mutation InviteUser($input: InviteUserInput! ) {
    inviteUser(input: $input) {
        id
    }
}`;

export interface InviteUserProps {
    id?: string;
    email: string;
    firstName: string;
    lastName: string;
    orgId: string;
    modules: Array<ModuleId>;
    groups: Array<string>;
    role: Role;
    cpGo: LicenseSystemAccessSettings;
    colorCard: LicenseSystemAccessSettings;
}
export interface InviteUserResponseBody {
    data: {
        inviteUser: InvitedUserPayload;
    };
    errors?: Array<GqlError>;
}

export const useInviteUser = () => {
    const context = useContext(AppContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (props: InviteUserProps) => {

            const input: InviteUserInput = {
                email: props.email,
                firstName: props.firstName,
                lastName: props.lastName,
                groups: props.groups,
                modules: props.modules,
                orgId: props.orgId,
                role: props.role as BackendTypeRole,
                licenseSystemAccess: {
                    colorProofGO: !props.cpGo.assignedLicenseSystems
                        ? undefined
                        : {
                            licenseSystemIds: props.cpGo.assignedLicenseSystems,
                            role: props.cpGo.role as LicenseSystemAccessRole,
                        },
                    colorCard: !props.colorCard.assignedLicenseSystems
                        ? undefined
                        : {
                            licenseSystemIds: props.colorCard.assignedLicenseSystems,
                            role: props.colorCard.role as LicenseSystemAccessRole,
                        },
                },
            };

            try {

                await (context.api.graphql({ query: mutation, variables: { input } }) as Promise<InviteUserResponseBody>);
                return 'success';
            } catch (e: any) {
                if (e.errors && e.errors.length && e.errors.length > 0 && e.errors[0].errorType === 'UserInviteAlreadyExists') {
                    return 'user_already_invited';
                }
                throw e;
            }
        },
        onSuccess: (result, variables) => {
            // on success is triggered also if the backend returns an error
            if (result === 'success') {
                void queryClient.invalidateQueries({ queryKey: ['getOrganization', variables.orgId] });
            }
        },
    });
};
